'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const supabase = createClient();

  // Helper: check if a session belongs to an admin user
  const checkAdminRole = (sess: any): boolean => {
    return sess?.user?.app_metadata?.role === 'admin';
  };

  useEffect(() => {
    console.log('[AuthContext] mounted');

    // Get initial session
    console.log('[AuthContext] before getSession()');
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('[AuthContext] after getSession() — session:', session ? 'EXISTS' : 'NULL', '| error:', error?.message ?? 'none');
      if (error) {
        console.error('[AuthContext] getSession error:', error.message);
        setAuthError(error.message);
      }
      setSession(session);
      setUser(session?.user ?? null);
      setIsAdmin(session ? checkAdminRole(session) : null);
      console.log('[AuthContext] session value received — user ID:', session?.user?.id ?? 'null');
      setLoading(false);
      console.log('[AuthContext] loading set to false (after getSession)');
    }).catch((err) => {
      console.error('[AuthContext] getSession threw:', err?.message ?? err);
      setAuthError(err?.message ?? String(err));
      setLoading(false);
      console.log('[AuthContext] loading set to false (getSession catch)');
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('[AuthContext] onAuthStateChange fired — event:', _event, '| session:', session ? 'EXISTS' : 'NULL', '| user ID:', session?.user?.id ?? 'null');
      setSession(session);
      setUser(session?.user ?? null);
      setIsAdmin(session ? checkAdminRole(session) : null);
      setLoading(false);
      console.log('[AuthContext] loading set to false (onAuthStateChange)');
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    authError,
    isAdmin,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
